import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function StatusPill({ active }) {
  const className = active
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${className}`}>
      {active ? 'Active' : 'Draft'}
    </span>
  );
}

function AudienceTags({ assignee, purchaser }) {
  const tags = [];
  if (assignee) tags.push('Assigned user');
  if (purchaser) tags.push('Purchaser');
  if (tags.length === 0) return <span className="text-xs text-amber-700">No audience selected</span>;
  return (
    <span className="text-xs text-gray-600">{tags.join(' + ')}</span>
  );
}

export default function SuperadminEmailJourneysIndex({ journeys = [] }) {
  const [pendingId, setPendingId] = useState(null);

  const handleToggleActive = (journey) => {
    setPendingId(journey.id);
    const action = journey.active ? 'deactivate' : 'activate';
    router.post(`/superadmin/email_journeys/${journey.id}/${action}`, {}, {
      onFinish: () => setPendingId(null),
    });
  };

  const handleDelete = (journey) => {
    if (!window.confirm(`Delete the journey "${journey.name}"? This cannot be undone.`)) return;
    setPendingId(journey.id);
    router.delete(`/superadmin/email_journeys/${journey.id}`, {
      onFinish: () => setPendingId(null),
    });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Email Journeys</h1>
            <p className="mt-1 text-sm text-gray-500">
              Automated email sequences that start when someone buys or is assigned a license.
            </p>
          </div>
          <Link
            href="/superadmin/email_journeys/new"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            New journey
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          {journeys.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No email journeys yet.</p>
              <Link
                href="/superadmin/email_journeys/new"
                className="mt-3 inline-flex text-sm font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Create the first one →
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {journeys.map((journey) => (
                <div key={journey.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">{journey.name}</p>
                        <StatusPill active={journey.active} />
                        {journey.active_enrollment_count > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-semibold">
                            {journey.active_enrollment_count} enrolled
                          </span>
                        )}
                      </div>

                      {journey.description && (
                        <p className="text-sm text-gray-500">{journey.description}</p>
                      )}

                      <p className="text-xs text-gray-600">
                        {journey.step_count} {journey.step_count === 1 ? 'step' : 'steps'}
                        {' · '}
                        {journey.email_step_count} {journey.email_step_count === 1 ? 'email' : 'emails'}
                        {' · '}
                        <AudienceTags assignee={journey.enroll_assignee} purchaser={journey.enroll_purchaser} />
                      </p>

                      <p className="text-xs text-gray-500">
                        {journey.license_names.length === 0
                          ? <span className="text-amber-700">No trigger licenses</span>
                          : <>Triggers on: {journey.license_names.join(', ')}</>}
                      </p>

                      {!journey.active && journey.activation_blockers.length > 0 && (
                        <p className="text-xs text-amber-700">
                          Needs {journey.activation_blockers.join(', ')} before it can be activated.
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 shrink-0">
                      <Link
                        href={`/superadmin/email_journeys/${journey.id}/edit`}
                        className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold rounded-lg text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition"
                      >
                        Edit
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleToggleActive(journey)}
                        disabled={pendingId === journey.id || (!journey.active && journey.activation_blockers.length > 0)}
                        title={
                          !journey.active && journey.activation_blockers.length > 0
                            ? `Needs ${journey.activation_blockers.join(', ')}`
                            : undefined
                        }
                        className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {journey.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(journey)}
                        disabled={pendingId === journey.id || journey.active || journey.active_enrollment_count > 0}
                        title={
                          journey.active
                            ? 'Deactivate before deleting'
                            : journey.active_enrollment_count > 0
                              ? 'Enrollments are still running'
                              : undefined
                        }
                        className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold rounded-lg text-red-700 bg-white border border-red-200 hover:bg-red-50 transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
